import { useCallback, useEffect, useState } from "react";
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import FullScreenDialog from "../../../Components/FullScreenDialog";
import CloseButton from "../../../Components/CloseButton";
import {
    CancelFormActionButton,
    PrimaryFormActionButton,
} from "../../../Features/Table/GenericDraggableTable/TableStyles";
import { getWorkOrder } from "../../../Api/WorkOrderApi";
import CODE from "../../../Static/Constants/StatusCodes";
import {
    autoApprovalNote,
    formatWorkOrderDateTime,
    workOrderStatusText,
    workOrderStatusTheme,
} from "./workOrderHelpers";
import { STATUS_PALETTE, STATUS_SURFACE } from "../statusPalette";
import { FORM_TOKENS } from "./WorkOrderFormShared";

// Full-page detail for one work order: the request, every shipment on it, and
// every asset on every shipment.
//
// FullScreenDialog, not a route -- the same choice, for the same reasons, as
// EntityDetailView on the Network tab: the back arrow, the scroll restore and
// the looks-like-a-page framing already behave the way users expect. A real
// route would also put the id in the URL, which is a separate decision about
// shareable links.
//
// Read through getWorkOrder rather than from the row it was opened from. The
// list row carries the header only; the detail endpoint returns the work order,
// its movements, its items and the resolved labels in ONE call, so nothing here
// is lazy-loaded and there is no second request to wait on.
//
// currentStateCode and currentEntityID on an item are the asset's state NOW,
// not at submit time. On a pending work order that is exactly what tells a
// checker whether custody has moved since the maker raised it -- which is the
// same thing the approve 409 would tell them, later and more expensively.

const Card = ({ children }) => (
    <Box
        sx={{
            bgcolor: FORM_TOKENS.surface,
            border: `1px solid ${FORM_TOKENS.cardBorder}`,
            borderRadius: "10px",
            p: 3,
            mb: 3,
        }}
    >
        {children}
    </Box>
);

const SectionTitle = ({ children }) => (
    <Typography
        sx={{
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: FORM_TOKENS.inkLabel,
            mb: 1.5,
            display: "block",
        }}
    >
        {children}
    </Typography>
);

const Field = ({ label, value, wide }) => (
    <Box sx={{ minWidth: 0, gridColumn: wide ? { sm: "1 / -1" } : undefined }}>
        <Typography sx={{ fontSize: "12px", lineHeight: "20px", color: FORM_TOKENS.inkLabel, mb: 0.25 }}>
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: "14px",
                lineHeight: "22px",
                fontWeight: 500,
                color: FORM_TOKENS.ink,
                overflowWrap: "anywhere",
            }}
        >
            {value}
        </Typography>
    </Box>
);

const StatusChip = ({ row }) => {
    const theme = workOrderStatusTheme(row);
    return (
        <Box
            component="span"
            data-testid="work-order-detail-status"
            sx={{
                display: "inline-block",
                px: 1.25,
                py: "3px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                color: theme.color,
                backgroundColor: theme.background,
                border: `1px solid ${theme.border}`,
            }}
        >
            {workOrderStatusText(row)}
        </Box>
    );
};

const ITEM_COLUMNS = [
    { key: "serialNumber", label: "Serial Number" },
    { key: "assetTypeCode", label: "Asset Type" },
    { key: "currentStateCode", label: "Current State" },
    { key: "sourceEntityName", label: "Source" },
    { key: "returnReasonCode", label: "Return Reason" },
    { key: "replacesSerialNumber", label: "Replaces" },
    { key: "loanReferenceID", label: "Loan Reference" },
];

// A movement and its lines. Plain markup rather than GenericDraggableTable:
// there is no paging, sorting, search or selection to hang off it, and pulling
// that machinery in would mean wiring five props to reach a read-only list.
const MovementCard = ({ movement, index }) => {
    const items = movement?.items || [];
    return (
        <Card>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 1.5,
                    flexWrap: "wrap",
                }}
            >
                <SectionTitle>
                    Movement {index + 1} · {movement?.movementTypeLabel || movement?.movementTypeCode || "-"}
                </SectionTitle>
                <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkLabel }}>
                    {items.length} asset{items.length === 1 ? "" : "s"}
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                    columnGap: 4,
                    rowGap: 2,
                    mb: 2,
                }}
            >
                <Field
                    label="Movement Date & Time"
                    value={formatWorkOrderDateTime(movement?.movementDateTime)}
                />
                <Field
                    label="Destination"
                    value={movement?.destinationEntityName || "-"}
                />
                <Field
                    label="Resulting State"
                    value={movement?.resultingStateCode || "-"}
                />
                {movement?.completesRecordID ? (
                    <Field label="Completes Record" value={movement.completesRecordID} />
                ) : null}
            </Box>

            {items.length === 0 ? (
                <Typography sx={{ fontSize: "14px", color: FORM_TOKENS.inkFaint }}>
                    No assets are attached to this movement.
                </Typography>
            ) : (
                <Box sx={{ overflowX: "auto" }}>
                    <Box
                        component="table"
                        sx={{
                            width: "100%",
                            borderCollapse: "collapse",
                            "& th, & td": {
                                textAlign: "left",
                                padding: "10px 12px",
                                borderBottom: `1px solid ${FORM_TOKENS.bandRule}`,
                                whiteSpace: "nowrap",
                            },
                            "& th": { fontSize: "12px", fontWeight: 600, color: FORM_TOKENS.inkLabel },
                            "& td": { fontSize: "14px", color: FORM_TOKENS.ink },
                        }}
                    >
                        <thead>
                            <tr>
                                {ITEM_COLUMNS.map((column) => (
                                    <th key={column.key}>{column.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, itemIndex) => (
                                <tr key={item.id ?? `${item.serialNumber}-${itemIndex}`}>
                                    {ITEM_COLUMNS.map((column) => (
                                        <td key={column.key}>
                                            {item[column.key] === null ||
                                            item[column.key] === undefined ||
                                            item[column.key] === ""
                                                ? "-"
                                                : item[column.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </Box>
            )}
        </Card>
    );
};

const WorkOrderDetailView = ({
    open,
    workOrder,
    onClose,
    // All three are already gated by the caller, which holds the permission
    // context and the current user id. Absent means "not offered", and the
    // caller renders the REASON beside the row where the reason matters.
    onReview,
    onEdit,
    onWithdraw,
    reviewBlockedReason = "",
}) => {
    // Seeded from the row so the page paints immediately, then replaced by the
    // detail response -- the row carries only the columns the table shows.
    const [detail, setDetail] = useState(workOrder);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const workOrderID = workOrder?.id;

    useEffect(() => {
        setDetail(workOrder);
    }, [workOrder]);

    useEffect(() => {
        if (!open || !workOrderID) return undefined;
        let cancelled = false;
        setIsLoading(true);
        setErrorMessage("");
        (async () => {
            const res = await getWorkOrder(workOrderID);
            if (cancelled) return;
            if (res?.responseStatus?.code === CODE.SUCCESS && res?.data) {
                setDetail(res.data);
            } else {
                // The row-seeded header stays on screen rather than blanking a
                // page the user is already reading; the message says why the
                // movements are missing.
                setErrorMessage(
                    res?.responseStatus?.msg || "Could not load this work order"
                );
            }
            setIsLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [open, workOrderID]);

    const handleEdit = useCallback(() => onEdit?.(detail), [onEdit, detail]);
    const handleReview = useCallback(() => onReview?.(detail), [onReview, detail]);
    const handleWithdraw = useCallback(() => onWithdraw?.(detail), [onWithdraw, detail]);

    if (!workOrder) return null;

    const movements = detail?.movements || [];
    const autoNote = autoApprovalNote(detail);

    return (
        <FullScreenDialog
            open={open}
            handleClose={onClose}
            containerStyles={{ bgcolor: FORM_TOKENS.panel }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", bgcolor: FORM_TOKENS.panel, minHeight: "100vh" }}>
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        bgcolor: FORM_TOKENS.panel,
                        px: 3,
                        pt: 2,
                        pb: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                            <IconButton
                                size="small"
                                onClick={onClose}
                                data-testid="work-order-detail-back"
                                aria-label="Back to the list"
                            >
                                <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                            <Typography variant="fw700s20lh24c2E2E2E" sx={{ overflowWrap: "anywhere" }}>
                                {detail?.workOrderReferenceID || "Work Order"}
                            </Typography>
                            <StatusChip row={detail} />
                            <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkFaint, ml: 1 }}>
                                {detail?.workOrderTypeLabel || detail?.workOrderTypeCode || ""}
                            </Typography>
                            {isLoading && <CircularProgress size={14} sx={{ ml: 1.5 }} />}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {onWithdraw && (
                                <CancelFormActionButton
                                    data-testid="work-order-detail-withdraw"
                                    onClick={handleWithdraw}
                                    sx={{ color: FORM_TOKENS.danger, borderColor: STATUS_PALETTE.error.border }}
                                >
                                    Withdraw
                                </CancelFormActionButton>
                            )}
                            {onEdit && (
                                <CancelFormActionButton
                                    data-testid="work-order-detail-edit"
                                    onClick={handleEdit}
                                >
                                    Edit
                                </CancelFormActionButton>
                            )}
                            {onReview && (
                                <PrimaryFormActionButton
                                    data-testid="work-order-detail-review"
                                    onClick={handleReview}
                                >
                                    Review
                                </PrimaryFormActionButton>
                            )}
                            <CloseButton onClick={onClose} />
                        </Box>
                    </Box>

                    {/* Said, not hidden. The button is absent because the
                        backend would refuse the click -- a user who can see the
                        row and holds the permission has no other way to find
                        that out. */}
                    {!onReview && reviewBlockedReason ? (
                        <Typography sx={{ fontSize: "12px", color: STATUS_PALETTE.warning.fg, mt: 1 }}>
                            {reviewBlockedReason}
                        </Typography>
                    ) : null}

                    <Divider sx={{ mt: 2, borderColor: FORM_TOKENS.fieldBorder }} />
                </Box>

                <Box sx={{ px: 3, pt: 3, pb: 4 }}>
                    {errorMessage ? (
                        <Typography sx={{ fontSize: 13, color: FORM_TOKENS.danger, mb: 2 }}>
                            {errorMessage}
                        </Typography>
                    ) : null}

                    {autoNote ? (
                        <Box
                            sx={{
                                border: `1px solid ${STATUS_PALETTE.warningSoft.border}`,
                                backgroundColor: STATUS_SURFACE.warning,
                                borderRadius: "8px",
                                p: 1.5,
                                mb: 3,
                            }}
                        >
                            <Typography sx={{ fontSize: "13px", color: STATUS_PALETTE.warning.fg }}>
                                {autoNote}
                            </Typography>
                        </Box>
                    ) : null}

                    <Card>
                        <SectionTitle>Request</SectionTitle>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
                                columnGap: 4,
                                rowGap: 2.5,
                            }}
                        >
                            <Field label="Reference ID" value={detail?.workOrderReferenceID || "-"} />
                            <Field label="Type" value={detail?.workOrderTypeLabel || detail?.workOrderTypeCode || "-"} />
                            <Field label="Status" value={workOrderStatusText(detail)} />
                            <Field label="Assets" value={detail?.itemCount ?? "-"} />
                            <Field label="Raised By" value={detail?.createdByName || "-"} />
                            <Field label="Raised On" value={formatWorkOrderDateTime(detail?.createdAt)} />
                            <Field
                                label="Reviewed By"
                                value={
                                    detail?.reviewedByName ||
                                    (autoNote ? "Not reviewed (auto-approved)" : "-")
                                }
                            />
                            <Field label="Reviewed On" value={formatWorkOrderDateTime(detail?.reviewedAt)} />
                            {/* The MAKER's remark and the CHECKER's are two
                                different things and are labelled as such. They
                                sat under one word before, which on a rejected
                                order meant the reason and the request's own
                                note read as the same voice. */}
                            <Field label="Maker's Remarks" value={detail?.remarks || "-"} wide />
                            {detail?.reviewRemark ? (
                                <Field label="Reviewer's Remark" value={detail.reviewRemark} wide />
                            ) : null}
                        </Box>
                    </Card>

                    {movements.length === 0 ? (
                        <Card>
                            <Typography sx={{ fontSize: "14px", color: FORM_TOKENS.inkFaint }}>
                                {isLoading
                                    ? "Loading the movements on this work order…"
                                    : "No movements are attached to this work order."}
                            </Typography>
                        </Card>
                    ) : (
                        movements.map((movement, index) => (
                            <MovementCard
                                key={movement.id ?? index}
                                movement={movement}
                                index={index}
                            />
                        ))
                    )}

                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <CancelFormActionButton
                            data-testid="work-order-detail-back-to-list"
                            onClick={onClose}
                        >
                            Back to list
                        </CancelFormActionButton>
                    </Box>
                </Box>
            </Box>
        </FullScreenDialog>
    );
};

export default WorkOrderDetailView;
